import React from 'react';
import { notifyAnalytics } from '../../notify-analytics';
import { filterMenuData } from '../../../../utils/menu/filterMenuData';
import { NavImage } from '../NavImage/NavImage';
import { NavLink } from '../NavLink/NavLink';
import { NavList } from '../../mobile-menu/NavList/NavList';
import { NavigationItemType } from '../itemTypes';
import { Heading } from '../../mobile-menu/Heading/Heading';
import { SHOP_DEPARTMENT, EXTRAS_DEPARTMENT, Category } from '../../constants';
import { allChildrenHaveBrandLogos } from '../../../utils/utils';

type MobileMenuItemProps = {
  child?: MenuItem[];
  children?: React.ReactNode;
  hierarchy?: string[];
  itemType?: NavigationItemType;
  label: string;
  level?: number;
  onClick?: (...args: unknown[]) => void;
  type?: string;
  url?: string;
  imageUrl?: string;
  activeTab?: string;
  shouldShowThumbnail?: boolean | string;
  isTabbable?: boolean;
};

export const MobileMenuItem = ({
  child = undefined,
  children = undefined,
  hierarchy: parentHierarchy = [],
  itemType = NavigationItemType.DEFAULT,
  label,
  level = 0,
  onClick = undefined,
  url = '',
  imageUrl = '',
  activeTab = '',
  shouldShowThumbnail = undefined,
  isTabbable = true,
}: MobileMenuItemProps) => {
  const isButton = child?.length > 0;
  const isAnchor = Boolean(url) && !isButton;

  // Check if we should show a thumbnail instead of chevron
  const shouldShowThumbnailValue =
    shouldShowThumbnail !== undefined
      ? Boolean(shouldShowThumbnail)
      : Boolean(isButton && activeTab && imageUrl);

  const filteredChildren = filterMenuData(child, ['MOBILE_WEB']) || [];

  const hierarchy =
    level === 0
      ? parentHierarchy
      : [
          ...parentHierarchy,
          label,
        ]; /* needed due to manipulation - might need to remove */

  const sendAnalytics = () => {
    notifyAnalytics(hierarchy.join('|'));
  };

  const advanceToChild = (...args: unknown[]) => {
    sendAnalytics();
    onClick?.(...args);
  };

  let showInlineMenu = false;
  let childItemType = NavigationItemType.DEFAULT;

  const filteredChildrenAllHaveBrandLogos =
    allChildrenHaveBrandLogos(filteredChildren);

  if (filteredChildrenAllHaveBrandLogos) {
    showInlineMenu = true;
    childItemType = NavigationItemType.BRAND_LOGOS;
  } else if (label === SHOP_DEPARTMENT) {
    showInlineMenu = true;
    childItemType = NavigationItemType.IMAGE_GRID;
  } else if (label === EXTRAS_DEPARTMENT) {
    showInlineMenu = true;
    childItemType = NavigationItemType.EXTRAS;
  }

  const showImage =
    itemType === NavigationItemType.IMAGE_ROW ||
    itemType === NavigationItemType.IMAGE_GRID ||
    itemType === NavigationItemType.BRAND_LOGOS;

  const getChild = (): React.ReactNode => {
    if (showImage) {
      return (
        <NavImage
          asAnchor={isAnchor}
          label={label}
          url={url}
          itemType={itemType}
          handleClick={isAnchor ? undefined : advanceToChild}
          imageUrl={imageUrl}
          tabIndex={isTabbable ? undefined : -1}
        />
      );
    }

    if (isAnchor) {
      return (
        <NavLink
          asAnchor
          label={label}
          url={url}
          shouldShowThumbnail={shouldShowThumbnailValue}
          imageUrl={imageUrl}
          handleClick={sendAnalytics}
          hierarchy={parentHierarchy.join('|')}
          tab={activeTab}
          tabIndex={isTabbable ? undefined : -1}
        />
      );
    }

    if (showInlineMenu) {
      const showHeading = !(activeTab === Category.SHOP && level === 0);
      return (
        <>
          {showHeading && <Heading label={label} />}
          <NavList
            inlineChild
            itemType={childItemType}
            testid="heading-sub-menu"
          >
            {filteredChildren.map(nestedChild => {
              const shouldShowNestedThumbnailImage =
                activeTab && nestedChild.imageUrl;
              return (
                <MobileMenuItem
                  key={`${level + 1}-${nestedChild.name}`}
                  level={level + 1}
                  label={nestedChild.name}
                  onClick={() => onClick?.(nestedChild)}
                  child={nestedChild.child}
                  itemType={childItemType}
                  type={nestedChild.type}
                  url={nestedChild.url}
                  imageUrl={nestedChild.imageUrl}
                  hierarchy={hierarchy}
                  activeTab={activeTab}
                  shouldShowThumbnail={shouldShowNestedThumbnailImage}
                  isTabbable={isTabbable}
                />
              );
            })}
          </NavList>
        </>
      );
    }

    if (isButton) {
      const isExtraItem =
        label === Category.SERVICES || label === Category.HELP;

      return (
        <NavLink
          isExtra={isExtraItem}
          label={label}
          handleClick={advanceToChild}
          shouldShowThumbnail={shouldShowThumbnailValue}
          shouldShowIcon={isExtraItem && !shouldShowThumbnailValue}
          imageUrl={imageUrl}
          tabIndex={isTabbable ? undefined : -1}
        />
      );
    }

    return <></>;
  };

  return (
    <>
      {getChild()}
      {children}
    </>
  );
};
